import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import {
  clearNotificationQuietHours,
  muteWorkspaceNotifications,
  readNotificationPreferences,
  setNotificationPreference,
  setNotificationQuietHours,
  unmuteWorkspaceNotifications,
} from '@/lib/api/generated/api';
import type {
  NotificationChannel,
  NotificationGroup,
  NotificationPreferences,
  QuietHours,
} from '@/lib/api/generated/model';

import { SETTINGS_COPY } from '../copy';
import { notificationKeys } from '../queryKeys';

/**
 * The signed-in person's notification preferences (service spec 118): the
 * group × channel matrix and the muted workspaces. Every write answers with
 * the whole document, which replaces the cache, so the page shows what the
 * service stored rather than what was asked for.
 */
export function usePreferences(enabled = true) {
  const queryClient = useQueryClient();
  const key = notificationKeys.preferences();

  const query = useQuery({
    queryKey: key,
    queryFn: async (): Promise<NotificationPreferences> => {
      const response = await readNotificationPreferences();
      if (response.status !== 200) throw new Error('preferences unavailable');
      return response.data;
    },
    enabled,
    retry: false,
  });

  const settle = {
    onSuccess: (next: NotificationPreferences) => {
      queryClient.setQueryData(key, next);
    },
    onError: () => {
      toast.error('That setting could not be changed.');
      void queryClient.invalidateQueries({ queryKey: key });
    },
  };

  const setCell = useMutation({
    mutationFn: async ({
      group,
      channel,
      enabled: on,
    }: {
      group: NotificationGroup;
      channel: NotificationChannel;
      enabled: boolean;
    }) => {
      const response = await setNotificationPreference(group, channel, {
        enabled: on,
      });
      if (response.status !== 200) throw new Error('refused');
      return response.data;
    },
    ...settle,
  });

  const setMuted = useMutation({
    mutationFn: async ({
      workspaceId,
      muted,
    }: {
      workspaceId: string;
      muted: boolean;
      /** Names the workspace in the refusal, when the caller knows it. */
      name?: string;
    }) => {
      const response = muted
        ? await muteWorkspaceNotifications(workspaceId)
        : await unmuteWorkspaceNotifications(workspaceId);
      if (response.status !== 200) throw new Error('refused');
      return response.data;
    },
    onSuccess: settle.onSuccess,
    onError: (_error, { name, muted }) => {
      toast.error(
        name
          ? SETTINGS_COPY.muteFailed(name, muted)
          : 'That setting could not be changed.',
      );
      void queryClient.invalidateQueries({ queryKey: key });
    },
  });

  // Quiet hours (spec 136 US3): a window to set, or `null` to turn them off.
  const setQuietHours = useMutation({
    mutationFn: async (window: QuietHours | null) => {
      const response = window
        ? await setNotificationQuietHours(window)
        : await clearNotificationQuietHours();
      if (response.status !== 200) throw new Error('refused');
      return response.data;
    },
    ...settle,
  });

  return {
    preferences: query.data,
    isPending: query.isPending,
    loadFailed: query.isError,
    refetch: query.refetch,
    setCell: setCell.mutate,
    setMuted: setMuted.mutate,
    setQuietHours: setQuietHours.mutate,
    saving: setCell.isPending || setMuted.isPending || setQuietHours.isPending,
  };
}
